import {Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField} from '@material-ui/core';
import {Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason} from '@material-ui/lab';
import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {Devices, User} from '../../../api/api';
import {deviceClient} from '../../../state/ducks/api-clients';
import {addUserDevice, deleteUserDevice, getDevicesAdmin} from '../../../state/ducks/devices/operations';
import {getUsers} from '../../../state/ducks/users/operations';
import {localized} from '../../../state/i18n/i18n';
import {showError, showSuccess} from '../../../state/snackbar/operations';
import {AppState} from '../../../state/store';
import JmTitle from '../../components/jm-title/jm-title';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchUsers: async () => (await getUsers())(dispatch),
  fetchDevices: async () => (await getDevicesAdmin())(dispatch),
  deleteUserDevice: async (deviceId: string, userId: string) => (await deleteUserDevice(deviceId, userId))(dispatch),
  addUserDevice: async (deviceId: string, userId: string) => (await addUserDevice(deviceId, userId))(dispatch),
  getSnackbarErrorDelete: () => showError(localized('ErrorRemovingDeviceFromUser'))(dispatch),
  getSnackbarErrorAdd: () => showError(localized('ErrorAssigningDeviceToUser'))(dispatch),
  getSnackbarSuccessDelete: (deviceId: string, userId: string) =>
    showSuccess(deviceId + localized('IsRemovedFromUser') + userId)(dispatch),
  getSnackbarSuccessAdd: (deviceId: string, userId: string) =>
    showSuccess(deviceId + localized('IsAssignedToUser') + userId)(dispatch),
});

const mapStateToProps = (state: AppState) => {
  return {
    users: state.userReducer.users,
    adminDevices: state.devicesReducer.adminDevices,
  };
};

type Props = ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
type AutocompleteProps = {
  deviceId: string;
  userId: string;
  userEmail: string;
};

interface State {
  keygen: string;
}

class Users extends PureComponent<Props, State> {
  constructor(props: Props, state: State) {
    super(props);

    this.state = {
      keygen: 'first',
    };
  }

  componentDidMount(): void {
    this.props.fetchUsers();

    this.props.fetchDevices();
  }

  private getUserDevices = (user: User) => {
    const devices: Devices[] = [];
    user.deviceUsers?.map((deviceUser) => {
      if (deviceUser.devices) return devices.push(deviceUser.devices);
      else return null;
    });

    return devices;
  };
  private getDefaultValue = (devices: Devices[], user: User): AutocompleteProps[] => {
    const options = this.getOptions(this.getUserDevices(user), user);
    const devicesOptions = this.getOptions(
      devices.filter((device) => options.find((option) => option.deviceId === device.deviceId)),
      user,
    );
    return devicesOptions;
  };

  private getOptions = (devices: Devices[], user: User): AutocompleteProps[] => {
    const options: {deviceId: string; userId: string; userEmail: string}[] = [];
    devices.map((device) => {
      return options.push({deviceId: device.deviceId ?? '', userId: user.id ?? '', userEmail: user.email ?? ''});
    });
    return options;
  };

  private handleAutoCompleteChange = async (
    event: React.ChangeEvent<{}>,
    value: {deviceId: string; userId: string} | {deviceId: string; userId: string; userEmail: string}[] | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<{deviceId: string; userId: string; userEmail: string}>,
  ) => {
    if (reason === 'remove-option') {
      try {
        if (!details) return;
        //Call delete DeviceUser
        if (details?.option.deviceId && details?.option.userId && details?.option.userEmail) {
          await deviceClient.deleteUserDevice(details.option.deviceId, details.option.userId).catch((e) => {
            throw e;
          });

          this.props.getSnackbarSuccessDelete(details.option.deviceId, details.option.userEmail);
        }
      } catch (error) {
        this.props.getSnackbarErrorDelete();
        this.props.fetchUsers().then(() => {
          this.setState({keygen: Math.random().toFixed(5)});
        });
      }
    } else if (reason === 'select-option') {
      if (details?.option.deviceId && details?.option.userId && details?.option.userEmail)
        try {
          //if call fails we need to remove the option from the list
          await deviceClient.addUserDevice(details.option.deviceId, details.option.userId).catch((e) => {
            throw e;
          }); // we need the rethrow to catch the error in the catch block
          this.props.getSnackbarSuccessAdd(details.option.deviceId, details.option.userEmail);
        } catch (e) {
          this.props.getSnackbarErrorAdd();
          this.props.fetchUsers().then(() => {
            this.setState({keygen: Math.random().toFixed(5)});
          });
        }
    }
  };

  public render() {
    return (
      <JmTitle title="Users" shouldPrefixBrandName={true}>
        <div style={{marginRight: '10em', marginLeft: '10em', marginTop: '10em'}}>
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">{localized('Email')}</TableCell>
                  <TableCell align="left">{localized('Devices')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.props.users?.map((user, index) => (
                  <TableRow key={user.id}>
                    <TableCell align="left">{user.email}</TableCell>
                    <TableCell align="left">
                      {this.props.adminDevices && user.id ? (
                        <Autocomplete
                          key={this.state.keygen + index}
                          multiple
                          id="tags-standard"
                          options={this.getOptions(this.props.adminDevices, user)}
                          getOptionLabel={(d) => (d.deviceId ? d.deviceId : '')}
                          defaultValue={this.getDefaultValue(this.props.adminDevices, user)}
                          //We need to filter the devices that the user has assigned to the devices in the list
                          filterSelectedOptions
                          getOptionSelected={(option, value) => option.deviceId === value.deviceId}
                          onChange={this.handleAutoCompleteChange}
                          renderInput={(params) => (
                            <TextField {...params} variant="standard" label="Devices" placeholder="DeviceId" />
                          )}
                        />
                      ) : (
                        <></>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </JmTitle>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Users);
